import { useState, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { FileText, Envelope, ChatText, CopySimple, Check, Lightning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";

export default function GeneratorPanel() {
  const { jobs, fetchJobs, generateContent, loading, documents, fetchDocuments } = useApp();
  const [selectedJobId, setSelectedJobId] = useState("");
  const [generatedContent, setGeneratedContent] = useState(null);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState("cover_letter");

  useEffect(() => {
    if (jobs.length === 0) fetchJobs();
    fetchDocuments();
  }, [fetchJobs, fetchDocuments, jobs.length]);

  const allJobs = jobs.filter(j => j.status !== "new" || j.scored).length > 0
    ? jobs.filter(j => j.status !== "new" || j.scored) : jobs;

  const handleGenerate = async () => {
    if (!selectedJobId) { toast.error("Select a job first"); return; }
    try {
      const result = await generateContent(selectedJobId, activeTab);
      setGeneratedContent(result);
      toast.success("Content generated");
    } catch (e) { toast.error("Generation failed"); }
  };

  const handleCopy = () => {
    if (generatedContent?.content) {
      navigator.clipboard.writeText(generatedContent.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Copied");
    }
  };

  const tabs = [
    { id: "cover_letter", label: "Cover Letter", icon: FileText },
    { id: "cold_email", label: "Cold Email", icon: Envelope },
    { id: "linkedin_message", label: "LinkedIn", icon: ChatText },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-[1000px]" data-testid="generator-panel">
      <div className="mb-8 animate-in">
        <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-white">AI Generator</h1>
        <p className="text-sm text-zinc-500 mt-2">Generate tailored application content powered by AI</p>
      </div>

      {/* Job Selector */}
      <div className="jp-card mb-5 animate-in delay-1">
        <div className="jp-card-header">
          <span className="jp-label">TARGET JOB</span>
        </div>
        <div className="p-5">
          <Select value={selectedJobId} onValueChange={setSelectedJobId}>
            <SelectTrigger className="bg-black/40 border-white/10 rounded-md h-10 text-sm" data-testid="gen-job-select">
              <SelectValue placeholder="Choose a job..." />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-white/10 rounded-md max-h-[300px]">
              {allJobs.slice(0, 50).map(job => (
                <SelectItem key={job.id} value={job.id} className="text-xs">
                  {job.title} @ {job.company}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#0A0A0A] border border-white/10 rounded-md h-11 w-full p-1 mb-5 animate-in delay-2">
          {tabs.map(tab => (
            <TabsTrigger key={tab.id} value={tab.id} data-testid={`gen-tab-${tab.id}`}
              className="flex-1 rounded-sm text-xs font-medium h-full
                data-[state=active]:bg-amber-500 data-[state=active]:text-black
                data-[state=inactive]:text-zinc-600">
              <tab.icon size={14} className="mr-1.5" weight="duotone" /> {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {tabs.map(tab => (
          <TabsContent key={tab.id} value={tab.id}>
            <div className="flex items-center gap-3 mb-5">
              <Button data-testid={`gen-submit-${tab.id}`} onClick={handleGenerate}
                disabled={loading[`gen-${tab.id}`] || !selectedJobId}
                className="bg-amber-500 text-black hover:bg-amber-400 rounded-md font-medium text-sm h-10 px-6">
                {loading[`gen-${tab.id}`] ? "Generating..." : <><Lightning size={16} className="mr-2" weight="fill" /> Generate</>}
              </Button>
              {generatedContent?.content && (
                <Button data-testid="copy-content-btn" onClick={handleCopy}
                  className="bg-transparent border border-white/10 text-zinc-400 hover:border-amber-500/40 hover:text-amber-500 rounded-md text-sm h-10">
                  {copied ? <><Check size={14} className="mr-2" /> Copied</> : <><CopySimple size={14} className="mr-2" /> Copy</>}
                </Button>
              )}
            </div>

            {/* Terminal Output */}
            <div className="jp-terminal" data-testid="gen-output">
              {loading[`gen-${activeTab}`] ? (
                <div>
                  <span className="text-emerald-500">$ </span>
                  <span>generating {tab.label.toLowerCase()}</span>
                  <span className="cursor" />
                </div>
              ) : generatedContent?.content ? (
                <div>
                  <div className="text-zinc-700 mb-3 text-[11px]">
                    // {generatedContent.doc_type?.replace('_', ' ').toUpperCase()} — {generatedContent.job_title} @ {generatedContent.company}
                  </div>
                  <div className="text-amber-100/90 whitespace-pre-wrap leading-relaxed">{generatedContent.content}</div>
                </div>
              ) : (
                <div>
                  <span className="text-emerald-500">$ </span>
                  <span className="text-zinc-600">select a job and click generate</span>
                  <span className="cursor" />
                </div>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Previous Generations */}
      {documents.length > 0 && (
        <div className="jp-card mt-6 animate-in delay-3" data-testid="previous-generations">
          <div className="jp-card-header">
            <span className="jp-label">HISTORY ({documents.length})</span>
          </div>
          <ScrollArea className="max-h-[280px]">
            {documents.slice(0, 20).map((doc, i) => (
              <div key={doc.id}
                className={`flex items-center justify-between px-5 py-3 ${i < 19 ? 'border-b border-white/5' : ''} cursor-pointer hover:bg-white/[0.02]`}
                onClick={() => setGeneratedContent(doc)}>
                <div className="flex items-center gap-2.5">
                  {doc.doc_type === "cover_letter" && <FileText size={14} className="text-amber-500" weight="duotone" />}
                  {doc.doc_type === "cold_email" && <Envelope size={14} className="text-sky-400" weight="duotone" />}
                  {doc.doc_type === "linkedin_message" && <ChatText size={14} className="text-purple-400" weight="duotone" />}
                  <span className="text-xs text-zinc-500 capitalize">{doc.doc_type?.replace('_', ' ')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] text-zinc-600 truncate max-w-[200px]">{doc.job_title} @ {doc.company}</span>
                  <span className="text-[10px] text-zinc-700 font-mono">{new Date(doc.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
